import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookMarked,
  GraduationCap,
  ListChecks,
} from "lucide-react";

/* Hero — final art direction. Dominant two-line central message framed by
   ten small educational "poster" widgets (5 left / 5 right, desktop+tablet)
   arranged in a staggered outer/inner rhythm that mirrors across both sides.
   CRITICAL BOUNDARY: posters live ONLY within the vertical range of the
   central hero block (top of headline → bottom of CTA row) — they never
   extend into the product glimpse or sections below. They sit BEHIND the
   central content layer, emerge gently on load, and quietly retreat on
   scroll. Mobile: clean centered stack, no posters. */

/* ---------- poster tiles: pure illustrated squares, reference-style ---------- */

/* Poster — wrapper carries position + the "emerge from behind the hero"
   entrance animation; the inner tile carries size + rotation so the keyframe
   transform never fights the tilt. */

const Poster = ({ testId, pos, tile, side = "left", delay = 0.5, children }) => (
  <div
    data-testid={testId}
    aria-hidden="true"
    className={`hero-widget-in pointer-events-none absolute select-none ${
      side === "right" ? "from-right" : "from-left"
    } ${pos}`}
    style={{ "--wd": `${delay}s` }}
  >
    <div
      className={`overflow-hidden rounded-[22px] shadow-[0_14px_30px_-12px_rgba(20,83,45,0.25)] ${tile}`}
    >
      {children}
    </div>
  </div>
);

const tileSvg = "block h-full w-full";

const MathArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#EAF5EE" />
    <path d="M50 18 L86 80 L14 80 Z" fill="#D5EBDD" stroke="#14532D" strokeWidth="2.5" strokeLinejoin="round" />
    <path d="M35 80 L50 54 L65 80" fill="none" stroke="#16A34A" strokeWidth="2" strokeDasharray="3 3" />
    <circle cx="50" cy="18" r="4" fill="#FACC15" />
    <path d="M18 22 a10 10 0 0 1 14 0" fill="none" stroke="#14532D" strokeWidth="2" strokeLinecap="round" />
    <text x="76" y="34" fontSize="15" fontWeight="800" fill="#16A34A" fontFamily="Manrope, sans-serif">π</text>
  </svg>
);

const BioArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#FEF9C3" />
    <path d="M32 10 C68 26 32 42 68 58 C80 63 78 76 66 84" fill="none" stroke="#16A34A" strokeWidth="3.4" strokeLinecap="round" />
    <path d="M68 10 C32 26 68 42 32 58 C20 63 22 76 34 84" fill="none" stroke="#14532D" strokeWidth="3.4" strokeLinecap="round" />
    <path d="M40 22 h20 M40 38 h20 M40 54 h20 M42 70 h16" stroke="#22C55E" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="68" cy="16" r="4" fill="#FACC15" />
  </svg>
);

const BooksArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#F1F6F2" />
    <rect x="18" y="62" width="64" height="13" rx="2.5" fill="#14532D" />
    <rect x="24" y="48" width="58" height="13" rx="2.5" fill="#16A34A" />
    <rect x="16" y="34" width="62" height="13" rx="2.5" fill="#FACC15" />
    <rect x="26" y="20" width="54" height="13" rx="2.5" fill="#22C55E" />
    <path d="M24 68 h52 M30 54 h46 M22 40 h50 M32 26 h42" stroke="#F8FAF7" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
  </svg>
);

const AtomArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#FFFFFF" />
    <ellipse cx="50" cy="50" rx="38" ry="15" fill="none" stroke="#14532D" strokeWidth="2.2" />
    <ellipse cx="50" cy="50" rx="38" ry="15" fill="none" stroke="#16A34A" strokeWidth="2.2" transform="rotate(60 50 50)" />
    <ellipse cx="50" cy="50" rx="38" ry="15" fill="none" stroke="#22C55E" strokeWidth="2.2" transform="rotate(-60 50 50)" />
    <circle cx="50" cy="50" r="7" fill="#16A34A" />
    <circle cx="82" cy="34" r="4.5" fill="#FACC15" />
    <circle cx="20" cy="72" r="3.5" fill="#14532D" />
  </svg>
);

const NotebookArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#EAF5EE" />
    <rect x="20" y="14" width="52" height="70" rx="5" fill="#FFFFFF" stroke="#14532D" strokeWidth="2.4" />
    <path d="M30 30 h32 M30 40 h32 M30 50 h22" stroke="#94A39B" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M30 60 h32" stroke="#16A34A" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M62 76 L84 34 l8 5 -22 42 -10 3 Z" fill="#FACC15" stroke="#B45309" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M84 34 l8 5 3-5 -8-5 Z" fill="#14532D" />
  </svg>
);

const TrophyArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#FEF9C3" />
    <path d="M32 18 h36 v18 a18 18 0 0 1-36 0 Z" fill="#FACC15" stroke="#14532D" strokeWidth="2.4" />
    <path d="M32 24 H20 a12 12 0 0 0 13 13 M68 24 h12 a12 12 0 0 1-13 13" fill="none" stroke="#14532D" strokeWidth="2.4" />
    <path d="M50 54 v10 M38 76 h24 M42 64 h16 l3 12 H39 Z" fill="#FACC15" stroke="#14532D" strokeWidth="2.2" strokeLinejoin="round" />
    <path d="M50 25 l3.4 6.8 7.6 1.1 -5.5 5.3 1.3 7.5 -6.8-3.6 -6.8 3.6 1.3-7.5 -5.5-5.3 7.6-1.1 Z" fill="#14532D" />
  </svg>
);

const FlaskArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#F1F6F2" />
    <path d="M40 14 h20 M45 14 v22 L24 76 a7 7 0 0 0 6.4 10 h39.2 a7 7 0 0 0 6.4-10 L55 36 V14" fill="#FFFFFF" stroke="#14532D" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M31 58 h38 L72 77 a3.5 3.5 0 0 1-3.2 5 H31.2 a3.5 3.5 0 0 1-3.2-5 Z" fill="#22C55E" opacity="0.45" />
    <circle cx="43" cy="68" r="3.4" fill="#16A34A" />
    <circle cx="55" cy="76" r="2.6" fill="#16A34A" />
    <circle cx="51" cy="50" r="2.4" fill="#FACC15" />
    <circle cx="60" cy="60" r="2" fill="#FACC15" />
  </svg>
);

const GraphArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#FFFFFF" />
    <path d="M14 84 H88" stroke="#E2E8F0" strokeWidth="2.6" strokeLinecap="round" />
    <rect x="22" y="58" width="11" height="26" rx="2.5" fill="#D5EBDD" />
    <rect x="40" y="46" width="11" height="38" rx="2.5" fill="#22C55E" />
    <rect x="58" y="34" width="11" height="50" rx="2.5" fill="#16A34A" />
    <path d="M18 44 L44 32 L64 24 L84 12" fill="none" stroke="#14532D" strokeWidth="2.6" strokeLinecap="round" strokeDasharray="1 6" />
    <circle cx="84" cy="12" r="4.5" fill="#FACC15" />
  </svg>
);

const ExamArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#EAF5EE" />
    <rect x="22" y="12" width="56" height="74" rx="5" fill="#FFFFFF" stroke="#14532D" strokeWidth="2.4" />
    <path d="M32 28 h24 M32 38 h24" stroke="#94A39B" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M32 50 l5 5 9-9" stroke="#16A34A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M32 64 l5 5 9-9" stroke="#16A34A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="64" cy="64" r="15" fill="#16A34A" />
    <text x="64" y="69.5" fontSize="13" fontWeight="800" fill="#fff" textAnchor="middle" fontFamily="Manrope, sans-serif">A+</text>
  </svg>
);

const AiArt = () => (
  <svg className={tileSvg} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
    <rect width="100" height="100" fill="#14532D" />
    <path d="M50 16 L57 41 L82 48 L57 55 L50 80 L43 55 L18 48 L43 41 Z" fill="#22C55E" />
    <circle cx="50" cy="48" r="6" fill="#F8FAF7" />
    <circle cx="20" cy="20" r="3.4" fill="#FACC15" />
    <circle cx="82" cy="78" r="3.4" fill="#FACC15" />
    <path d="M24 24 L40 38 M78 74 L60 58" stroke="#F8FAF7" strokeWidth="1.8" strokeLinecap="round" opacity="0.8" />
  </svg>
);

/* ---------- hero ---------- */

const HeroSection = ({ loggedIn }) => {
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  // Scroll retreat: posters drift back/down and fade as the hero leaves view.
  // Base scale mirrors the responsive Tailwind scale classes on the rails so
  // the inline transform never overrides them with scale(1).
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const baseScale = () => {
      const w = window.innerWidth;
      if (w >= 1536) return 1;
      if (w >= 1280) return 0.9;
      if (w >= 1024) return 0.65;
      return 0.5;
    };
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const sy = window.scrollY;
        const transform = `translateY(${sy * 0.35}px) scale(${baseScale() * Math.max(0.72, 1 - sy * 0.0008)})`;
        const opacity = String(Math.max(0, 1 - sy / 280));
        for (const el of [leftRef.current, rightRef.current]) {
          if (!el) continue;
          el.style.transform = transform;
          el.style.opacity = opacity;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-transparent px-4 pb-10 pt-12 sm:px-6 sm:pb-14 md:pt-20"
    >

      {/* Framed composition: poster columns share the EXACT vertical range of
          the central hero block (headline top → CTA bottom) — nothing below */}
      <div className="relative">
        {/* Poster columns — behind the central hero layer (z-10 < z-20).
            Rails keep the full desktop geometry and SCALE DOWN per breakpoint
            (transform-origin at the outer top corner) so tiles are always
            complete inside the gutter — never sliced by the centre card or
            the viewport. Inner-depth posters join at lg; all five at xl. */}
        <div
          ref={leftRef}
          data-testid="hero-poster-column-left"
          className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 hidden w-[360px] origin-top-left md:block md:scale-50 lg:scale-[0.65] xl:scale-90 2xl:scale-100"
        >
          {/* staggered frame: outer → inner → outer → inner → outer, top → CTA */}
          <Poster testId="hero-poster-left-1" side="left" delay={0.45} pos="left-[3%] top-[1%]" tile="h-[95px] w-[95px] -rotate-6 opacity-95 lg:h-[130px] lg:w-[130px]"><MathArt /></Poster>
          <Poster testId="hero-poster-left-2" side="left" delay={0.53} pos="left-[34%] top-[15%] lg:left-[36%] xl:left-[31%] 2xl:left-[36%]" tile="hidden h-[75px] w-[75px] rotate-4 opacity-90 lg:block lg:h-[100px] lg:w-[100px]"><BioArt /></Poster>
          <Poster testId="hero-poster-left-3" side="left" delay={0.61} pos="left-[1%] top-[38%] lg:left-[2%]" tile="h-[105px] w-[105px] -rotate-3 opacity-95 lg:h-[140px] lg:w-[140px]"><BooksArt /></Poster>
          <Poster testId="hero-poster-left-4" side="left" delay={0.69} pos="left-[30%] top-[60%] lg:left-[33%]" tile="hidden h-[70px] w-[70px] rotate-6 opacity-90 lg:block lg:h-[95px] lg:w-[95px]"><FlaskArt /></Poster>
          <Poster testId="hero-poster-left-5" side="left" delay={0.77} pos="left-[4%] top-[76%]" tile="h-[85px] w-[85px] -rotate-6 opacity-85"><NotebookArt /></Poster>
        </div>
        <div
          ref={rightRef}
          data-testid="hero-poster-column-right"
          className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 hidden w-[360px] origin-top-right md:block md:scale-50 lg:scale-[0.65] xl:scale-90 2xl:scale-100"
        >
          {/* mirrored rhythm: same vertical beats, same outer/inner cadence */}
          <Poster testId="hero-poster-right-1" side="right" delay={0.45} pos="right-[3%] top-[1%]" tile="h-[90px] w-[90px] rotate-6 opacity-95 lg:h-[125px] lg:w-[125px]"><TrophyArt /></Poster>
          <Poster testId="hero-poster-right-2" side="right" delay={0.53} pos="right-[34%] top-[16%] lg:right-[36%] xl:right-[31%] 2xl:right-[36%]" tile="hidden h-[75px] w-[75px] -rotate-4 opacity-90 lg:block lg:h-[100px] lg:w-[100px]"><AtomArt /></Poster>
          <Poster testId="hero-poster-right-3" side="right" delay={0.61} pos="right-[1%] top-[39%] lg:right-[2%]" tile="h-[100px] w-[100px] rotate-5 opacity-95 lg:h-[135px] lg:w-[135px]"><GraphArt /></Poster>
          <Poster testId="hero-poster-right-4" side="right" delay={0.69} pos="right-[30%] top-[61%] lg:right-[33%]" tile="hidden h-[70px] w-[70px] -rotate-6 opacity-90 lg:block lg:h-[95px] lg:w-[95px]"><ExamArt /></Poster>
          <Poster testId="hero-poster-right-5" side="right" delay={0.77} pos="right-[4%] top-[77%]" tile="h-[85px] w-[85px] rotate-6 opacity-85"><AiArt /></Poster>
        </div>

        {/* Central content — solid background so posters tuck behind it.
            Narrower on md–xl so the gutters fit the scaled poster rails. */}
        <div className="relative z-20 mx-auto max-w-[600px] rounded-3xl bg-[#F8FAF7] px-4 py-4 text-center lg:max-w-2xl xl:max-w-[840px] 2xl:max-w-4xl">
        <span
          data-testid="hero-eyebrow"
          className="hero-anim inline-flex items-center gap-2 rounded-full border border-[#14532D]/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold tracking-wide text-[#14532D] sm:text-[14px]"
        >
          <span className="pulse-dot inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A34A]" />
          Your all-in-one study buddy for CBSE &amp; ICSE
        </span>
        <h1
          data-testid="hero-headline"
          className="hero-anim mx-auto mt-6 max-w-4xl font-display text-[40px] font-extrabold leading-[1.02] tracking-[-0.025em] text-[#17201A] sm:text-[62px] sm:leading-[1.0] sm:tracking-[-0.03em] lg:text-[78px] lg:leading-[0.98] lg:tracking-[-0.035em]"
          style={{ animationDelay: "0.1s" }}
        >
          What will you
          <br />
          <span className="text-[#16A34A]">practice today?</span>
        </h1>
        <p
          data-testid="hero-subtext"
          className="hero-anim mx-auto mt-6 max-w-[650px] font-body text-[16px] leading-[1.5] text-[#647067] sm:text-[17px] sm:leading-[1.6] lg:text-[20px]"
          style={{ animationDelay: "0.22s" }}
        >
          Practice questions, understand your mistakes, strengthen weak areas,
          and prepare smarter with a learning system built around what you
          need to improve.
        </p>
        {/* CTAs: horizontal on desktop/tablet, vertically stacked on mobile */}
        <div
          className="hero-anim mx-auto mt-8 flex w-full max-w-[340px] flex-col items-center justify-center gap-3 md:mt-9 md:max-w-none md:flex-row md:gap-4"
          style={{ animationDelay: "0.32s" }}
        >
          <Link
            to={loggedIn ? "/practice-setup" : "/signup"}
            data-testid="hero-primary-cta"
            data-magnetic="true"
            className="inline-flex h-[50px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-[#16A34A] px-8 text-base font-bold text-white shadow-md transition-[background-color,transform] duration-200 hover:bg-[#15803D] active:scale-[0.98] md:w-auto"
          >
            Start Practicing
            <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href="#how-it-works"
            data-testid="hero-secondary-cta"
            className="group inline-flex h-[50px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-[#E2E8F0] bg-white px-7 text-base font-semibold text-[#17201A] shadow-sm transition-[border-color,background-color,transform] duration-200 hover:border-[#14532D]/30 hover:bg-white active:scale-[0.98] md:w-auto"
          >
            Explore Features
            <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-y-0.5" />
          </a>
        </div>
        </div>
      </div>

      {/* Trust signals — quiet, factual */}
      <div
        data-testid="hero-trust-signals"
        className="relative z-20 mx-auto mt-8 flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-medium tracking-wide text-[#647067] opacity-85 sm:text-sm"
      >
        <span className="inline-flex items-center gap-2">
          <BookMarked className="h-4 w-4 text-[#16A34A]" />
          CBSE &amp; ICSE aligned
        </span>
        <span className="inline-flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-[#16A34A]" />
          Classes 9–12
        </span>
        <span className="inline-flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-[#16A34A]" />
          Step-by-step solutions
        </span>
      </div>
    </section>
  );
};

export default HeroSection;
